import ErrorMessage from '@/components/form-control/ErrorMessage'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { DATA_PAGINATION } from '@/types'
import {
  debounce,
  FormControl,
  InputLabel,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'
import clsx from 'clsx'
import { memo, useEffect, useMemo, useState } from 'react'
import { useController, get } from 'react-hook-form'
import { useSelector } from 'react-redux'
import ReactSelect, {
  components,
  DropdownIndicatorProps,
  GroupBase,
  OptionProps,
  StylesConfig,
} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { StateManagerProps } from 'react-select/dist/declarations/src/stateManager'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  UnCheckIcon,
  CloseIcon,
} from '../../icons'
import classes from './styles.module.scss'

const ClearIndicator = (props: any) => {
  const themeMode = useSelector(selectTheme)

  return (
    <components.ClearIndicator {...props}>
      <CloseIcon
        sx={{
          fontSize: '16px',
          color: themeMode === mode_type.dark ? '#fff' : '#000',
          cursor: 'pointer',
        }}
      />
    </components.ClearIndicator>
  )
}

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  const themeMode = useSelector(selectTheme)

  return (
    <components.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen ? (
        <ArrowUpIcon
          sx={{
            color: themeMode === mode_type.dark ? '#fff' : '#000',
          }}
        />
      ) : (
        <ArrowDownIcon
          sx={{
            color: themeMode === mode_type.dark ? '#fff' : '#000',
          }}
        />
      )}
    </components.DropdownIndicator>
  )
}

const CustomOption =
  (bindKey: string = 'id', themeMode: any) =>
  (props: OptionProps) => {
    const { getValue, data } = props
    const isSelected = getValue().some(
      (item: any) => item?.[bindKey] === (data as any)?.[bindKey],
    )

    return (
      <components.Option {...props}>
        <Stack direction={'row'} gap={1} alignItems={'center'} p="0px">
          {isSelected ? (
            <CheckIcon
              sx={{
                color: '#000',
              }}
            />
          ) : (
            <UnCheckIcon />
          )}

          <Typography
            style={{
              color: isSelected
                ? '#004744'
                : themeMode === mode_type.dark
                  ? '#fff'
                  : '#000',
            }}
          >
            {props.children}
          </Typography>
        </Stack>
      </components.Option>
    )
  }
interface PAGINATION {
  page: number
  size: number
  totalCount: number
  totalPage: number
}

interface Props extends StateManagerProps {
  sx?: SxProps
  isClearable?: boolean
  label?: string
  name: string
  form?: any
  required?: boolean
  errorsName?: any
  bindKey?: string
  bindLabel?: string
  isHasMore?: boolean
  filterFunc?: boolean
  loadOptionInit?: boolean
  refreshWhenOpen?: boolean
  className?: string
  mapMode?: boolean
  height?: string | number
  minWidth?: string | number
  maxWidth?: string | number
  getOptions?: (
    PageIndex?: number,
    PageSize?: number,
    Keyword?: string,
  ) => Promise<DATA_PAGINATION | null | undefined> | null | undefined
  isMulti?: boolean
  hasLoadMoreItem?: boolean
  zIndex?: number
  isCreatable?: boolean
}

const Select = memo(
  ({
    sx,
    label,
    name,
    form,
    required,
    errorsName,
    placeholder,
    className,
    bindKey,
    refreshWhenOpen,
    bindLabel,
    isHasMore,
    filterFunc = true,
    getOptions,
    loadOptionInit,
    onChange,
    value,
    isDisabled,
    mapMode,
    height = '50px',
    minWidth = '377px',
    maxWidth,
    isMulti = false,
    hasLoadMoreItem = true,
    zIndex,
    isCreatable = false,
    isClearable,
    ...rest
  }: Props) => {
    const SelectComponent = isCreatable ? (CreatableSelect as any) : ReactSelect

    const { control } = form || { control: null }

    const {
      field: {
        onChange: onChangeCrl,
        onBlur: onBlurCrl,
        value: valueCrl,
        ref: refCrl,
      },
      formState: { errors },
    } = control
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useController({ name, control })
      : {
          field: { onChange, value, onBlur: undefined, ref: undefined },
          formState: { errors: undefined },
        }

    const [focused, setFocused] = useState(false)
    const [keyword, setKeyword] = useState<string>()
    const [options, setOptions] = useState<any[]>([])
    const [metadata, setMetadata] = useState<PAGINATION>()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const getMetaFromData = (
      data: DATA_PAGINATION | null | undefined,
    ): PAGINATION | undefined => {
      if (!data) return undefined
      return {
        page: data.page,
        size: data.size,
        totalCount: data.totalCount,
        totalPage: data.totalPage,
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onMenuScrollToBottom = debounce(async () => {
      if (
        isLoading ||
        !isHasMore ||
        !getOptions ||
        !metadata ||
        metadata.page >= metadata.totalPage ||
        !options?.length ||
        !hasLoadMoreItem
      )
        return
      setIsLoading(true)
      const newPage = metadata.page + 1
      const data = await getOptions(newPage, metadata.size, keyword)
      setOptions((pre) => pre.concat(data?.data || []))
      setMetadata(getMetaFromData(data))
      setIsLoading(false)
    }, 200)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onInputChange = debounce(async (newValue: string) => {
      if (!isHasMore || !getOptions || (!keyword && !newValue)) return
      setKeyword(newValue)

      if (!isLoading) {
        setIsLoading(true)
        const data = await getOptions(1, metadata?.size, newValue)
        setMetadata(getMetaFromData(data))
        setOptions(data?.data || [])
        setIsLoading(false)
      }
      //====
    }, 200)

    const onMenuOpen = async () => {
      if (isLoading || (options?.length && !refreshWhenOpen) || !getOptions)
        return
      setIsLoading(true)
      const data = await getOptions(1, metadata?.size)
      setMetadata(getMetaFromData(data))
      setOptions(data?.data || [])
      setIsLoading(false)
      setKeyword('')
    }

    useEffect(() => {
      const getOptionsInit = async () => {
        if (!getOptions) return
        setIsLoading(true)
        const data = await getOptions(1, metadata?.size)
        setMetadata(getMetaFromData(data))
        setOptions(data?.data || [])
        setIsLoading(false)
      }
      if (loadOptionInit) {
        getOptionsInit()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const shrink = useMemo(() => {
      return focused || !!valueCrl
    }, [focused, valueCrl])

    const themeMode = useSelector(selectTheme)
    const customStyles = (
      theme?: Theme,
      isError?: boolean,
      height?: number | string,
      hasLabel?: boolean,
      isMulti?: boolean,
    ): StylesConfig<any, boolean, GroupBase<unknown>> => ({
      indicatorSeparator: () => ({
        display: 'none',
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        '> div': {
          paddingRight: 0,
        },
      }),
      menuList: (provided) => ({
        ...provided,
        padding: 0, // Remove padding from the menu list
      }),
      option: (provided, state) => ({
        ...provided,
        fontStyle: 'normal',
        fontSize: 12,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        color: themeMode === mode_type.dark ? '#fff' : '#000',
        backgroundColor: themeMode === mode_type.dark ? '#1a3746' : '#fff',
        ...(state.isSelected && {
          backgroundColor: themeMode === mode_type.dark ? '#99b5b4' : '#66918f',
        }),
        ...(state.isFocused && {
          backgroundColor: themeMode === mode_type.dark ? '#0a2939' : '#D9D9D9',
        }),
        ...(state.isDisabled && {
          backgroundColor: themeMode === mode_type.dark ? '#0a2939' : '#D9D9D9',
        }),
      }),
      placeholder: (provided) => ({
        ...provided,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
      valueContainer: (provided) => ({
        ...provided,
        paddingLeft: 0,
        ...(hasLabel && {
          paddingTop: isMulti ? '20px' : 0,
          paddingBottom: isMulti ? '8px' : 0,
          top: isMulti ? 0 : '8px',
        }),
      }),
      singleValue: (provided) => ({
        ...provided,
        fontSize: 12,
        color: themeMode === mode_type.dark ? '#fff' : '#000',
        marginLeft: 0,
        marginRight: 0,
      }),
      input: (provided) => ({
        ...provided,
        fontSize: 12,
        color: themeMode === mode_type.dark ? '#fff' : '#000',
      }),
      control: (provided) => ({
        ...provided,
        borderRadius: '4px',
        border: 'none',
        boxShadow: 'none',
        minHeight: height || '50px',
        backgroundColor: 'background.overrideDefault',
        padding: '0px 16px',
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: zIndex ? zIndex : 2,
      }),
    })

    const fieldError = get(errors, name)

    return (
      <>
        <FormControl
          variant="standard"
          sx={{
            minWidth: minWidth,
            maxWidth: maxWidth && maxWidth,
            minHeight: height,
            marginTop: '8px',
            backgroundColor: isDisabled
              ? '#d9d9d9'
              : 'background.overrideDefault',
            borderRadius: '4px',
          }}
          className={clsx(
            className,
            'className',
            { [classes.mapMode]: mapMode },
            { [classes.errors]: !!fieldError },
          )}
        >
          <FormControl
            disabled={isDisabled}
            variant="standard"
            sx={{
              minHeight: height,
              padding: 0,
              backgroundColor: 'background.overrideDefault',
              borderRadius: '4px',
              overflow: 'hidden',
              ...sx,
            }}
          >
            {label && (
              <InputLabel
                required={required}
                shrink={shrink}
                focused={shrink}
                className={classes.label}
                sx={{
                  color: themeMode === mode_type.dark ? '#fff' : '#000',
                  '& .MuiInputLabel-asterisk': {
                    color: 'red',
                  },
                  '&.Mui-focused': {
                    color: themeMode === mode_type.dark ? '#fff' : '#000',
                  },
                }}
              >
                {label}
              </InputLabel>
            )}

            <SelectComponent
              isMulti={isMulti}
              menuPosition="fixed"
              styles={customStyles(
                undefined,
                !!(fieldError?.message || errorsName),
                height,
                Boolean(label),
                isMulti,
              )}
              getOptionValue={(option: any) => option[bindKey || 'id']}
              getOptionLabel={(option: any) => option[bindLabel || 'label']}
              components={{
                DropdownIndicator,
                ClearIndicator,
                ...(isMulti && { Option: CustomOption(bindKey, themeMode) }),
              }}
              isLoading={isLoading}
              options={options}
              filterOption={
                filterFunc
                  ? (option: any, input: string) => {
                      return (option?.label || '')
                        .toLocaleLowerCase()
                        .includes((input || '').toLocaleLowerCase())
                    }
                  : () => true
              }
              onMenuOpen={onMenuOpen}
              onInputChange={onInputChange}
              onMenuScrollToBottom={onMenuScrollToBottom}
              isDisabled={isDisabled}
              onChange={(val: any, actionMeta: any) => {
                onChangeCrl && onChangeCrl(val, actionMeta)
                if (control && onChange) onChange(val, actionMeta)
              }}
              onBlur={() => {
                onBlurCrl && onBlurCrl()
                setFocused(false)
              }}
              onFocus={() => setFocused(true)}
              value={valueCrl ?? null}
              ref={refCrl}
              {...rest}
              {...(isCreatable
                ? {
                    getNewOptionData: (
                      inputValue: string,
                      optionLabel: React.ReactNode,
                    ) => ({
                      [bindKey || 'id']: inputValue || optionLabel,
                      [bindLabel || 'label']: optionLabel,
                      __isNew__: true,
                    }),
                    formatCreateLabel: (val: string) => `Create "${val}"`,
                  }
                : {})}
              placeholder={shrink || !label ? (placeholder ?? '') : ''}
              hideSelectedOptions={false}
              isClearable={isClearable}
            />
          </FormControl>
        </FormControl>
        <ErrorMessage message={fieldError?.message || errorsName} />
      </>
    )
  },
)

export default Select
